import { useEffect, useState, useRef } from 'react';
import { useWeb3React } from '@web3-react/core';
import { Grid } from 'antd';
import { Button } from 'react-bootstrap';
import { toast, ToastContainer } from 'react-toastify';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { BigNumber, ethers } from 'ethers';

import { useMarketplaceContract, useMCRT } from 'hooks/useContract';
import { fetchNftInfoAsync } from 'state/action';
import { useNFTMarketplaceState } from 'state/hooks';
import { getMarketplaceAddress, getShortenAddress } from 'utils/addressHelpers';
import { getNftContractAddress } from 'state/nftMarketplace';
import {
  getNameBgColor,
  getRarityBgColor,
  getRarityFrame,
} from 'utils/nftHelper';
import { SellModal } from 'components/SellModal';
import NftMedia from 'components/NftMedia';

import bnbLogo from 'assets/icon-bnb.png';
import mcrtLogo from 'assets/icon-mcrt.png';
import { ReactComponent as Badge } from 'assets/badge.svg';

// styles
import './index.scss';
import GasFeeHelpContainer from 'components/GasFeeHelpContainer';
import HelpModal from 'components/HelpModal/HelpModal';

const NFTDetail = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { account } = useWeb3React();
  const breakpoint = Grid.useBreakpoint();
  const { selectedNft } = useNFTMarketplaceState();
  const params = useParams();
  const tokenId = params.id;
  const contractAddressFromUrl = params.nft_address ?? params.nftAddress;
  const contractAddress = contractAddressFromUrl || selectedNft?.contractAddress || getNftContractAddress() || '';

  const [isSellModal, setSellModal] = useState(false);
  const [nftData, setNFTData] = useState(null);
  const [isOwned, setIsOwned] = useState(false);
  const [bnbPrice, setBNBPrice] = useState(null);
  const [mcrtPrice, setMCRTPrice] = useState(null);
  const [isMCRT, setIsMCRT] = useState(null);
  const [isHelpModalOpen, setIsHelpModalOpen] = useState(false);
  const effectRunRef = useRef(0);

  const resolvedContract = (contractAddressFromUrl && String(contractAddressFromUrl).startsWith('0x'))
    ? contractAddressFromUrl
    : (getNftContractAddress() || '');

  let downloadedImg = '';

  const magicMCRT = useMCRT();
  const ctMarketplace = useMarketplaceContract();

  // const contractAddress = getNftContractAddress();

  const closeSellModal = () => {
    setSellModal(false);
    navigate(0);
  };

  const onSellItem = () => {
    setSellModal(true);
  };

  const goBack = () => {
    const prevPath = localStorage.getItem('prevPath');

    if (prevPath) {
      navigate(prevPath);
    }
  };

  const onBuyItem = async () => {
    try {
      if (!account) {
        toast.error('Please connect your wallet first.');
        return;
      }
      const addr = (contractAddress && String(contractAddress).startsWith('0x') && String(contractAddress).length >= 40)
        ? contractAddress
        : (getNftContractAddress() || '');
      if (!addr || !addr.startsWith('0x')) {
        toast.error('Invalid contract address. Please try again from the marketplace list.');
        return;
      }
      if (effectiveAuctionData) {
        const today = new Date();

        let startingPrice = Number(effectiveAuctionData.startingPrice).toLocaleString(
          'fullwide',
          {
            useGrouping: false,
          },
        );

        let endingPrice = Number(effectiveAuctionData.endingPrice).toLocaleString(
          'fullwide',
          {
            useGrouping: false,
          },
        );

        const offset = ethers.BigNumber.from(startingPrice).gt(
          ethers.BigNumber.from(endingPrice),
        )
          ? -1000
          : 1000;

        if (!effectiveAuctionData.isMCRT) {
          const bnbAmt = ethers.BigNumber.from(`${startingPrice}`).add(
            ethers.BigNumber.from(
              Math.min(
                effectiveAuctionData.duration,
                Math.ceil(today.getTime() / 1000.0 + offset - effectiveAuctionData.startAt),
              ),
            )
              .mul(
                ethers.BigNumber.from(`${endingPrice}`).sub(
                  ethers.BigNumber.from(`${startingPrice}`),
                ),
              )
              .div(ethers.BigNumber.from(effectiveAuctionData.duration)),
          );

          ctMarketplace.methods
            .bid(addr, tokenId)
            .send({
              from: account,
              value: bnbAmt.toString(),
            })
            .on('receipt', () => {
              toast.success('The NFT has been purchased successfully.');
              setTimeout(() => navigate(0), 5000);
            })
            .on('error', () => {
              if (account) {
                toast.error('Your purchase has been failed.');
              } else {
                toast.error('Please connect your wallet first.');
              }
            });
        } else {
          const mcrtAmt = ethers.BigNumber.from(`${startingPrice}`).add(
            ethers.BigNumber.from(
              Math.min(
                effectiveAuctionData.duration,
                Math.ceil(today.getTime() / 1000.0 + offset - effectiveAuctionData.startAt),
              ),
            )
              .mul(
                ethers.BigNumber.from(`${endingPrice}`).sub(
                  ethers.BigNumber.from(`${startingPrice}`),
                ),
              )
              .div(ethers.BigNumber.from(effectiveAuctionData.duration)),
          );

          const allowance = await magicMCRT.methods
            .allowance(account, getMarketplaceAddress())
            .call();
          if (
            BigNumber.from(String(allowance)).lt(
              BigNumber.from(String(mcrtAmt)),
            )
          ) {
            await magicMCRT.methods
              .approve(getMarketplaceAddress(), mcrtAmt)
              .send({
                from: account,
              });
          }
          // example of successful bid:
          // https://bscscan.com/tx/0xa8e781dd08c66954120d55b0777b9503b4860e3684246b5479085fc692b44ac5
          ctMarketplace.methods
            .bidMCRT(addr, tokenId, mcrtAmt)
            .send({
              from: account,
            })
            .on('receipt', () => {
              toast.success('The NFT has been purchased successfully.');
              setTimeout(() => navigate(0), 5000);
            })
            .on('error', () => {
              if (account) {
                toast.error('Your purchase has been failed.');
              } else {
                toast.error('Please connect your wallet first.');
              }
            });
        }
      } else {
        toast.error('Price data is not available. Please try again from the marketplace list.');
      }
    } catch (error) {
      console.warn(error);
    }
  };

  const onCancelItem = async () => {
    if (!contractAddress || !String(contractAddress).startsWith('0x')) {
      toast.error('Invalid contract address.');
      return;
    }
    ctMarketplace.methods
      .cancelAuction(contractAddress, tokenId)
      .send({
        from: account,
      })
      .on('receipt', () => {
        toast.success('Your auction has been cancelled successfully.');
        setTimeout(() => navigate(0), 5000);
      })
      .on('error', () => {
        toast.error('Your auction cancel has been failed.');
      });
  };

  useEffect(() => {
    const seller = nftData?.seller ?? selectedNft?.seller ?? selectedNft?.owner;
    setIsOwned(
      !!(account && seller && String(seller).toUpperCase() === String(account).toUpperCase()),
    );
    startDownload();
  }, [selectedNft, account, nftData]);

  // function imageReceived() {
  //   const canvas = document.createElement('canvas');
  //   const ctx = canvas.getContext('2d');

  //   canvas.width = 318;
  //   canvas.height = 318;

  //   var frame = new Image();
  //   ctx.drawImage(
  //     downloadedImg,
  //     0,
  //     0,
  //     downloadedImg.width,
  //     downloadedImg.height,
  //     3,
  //     3,
  //     canvas.width - 3,
  //     canvas.height - 3,
  //   );
  //   frame.src = getRarityFrame(rarity);
  //   ctx.drawImage(
  //     frame,
  //     0,
  //     0,
  //     frame.width,
  //     frame.height,
  //     0,
  //     0,
  //     canvas.width,
  //     canvas.height,
  //   );

  //   console.log(document.getElementById('avatar'));

  //   document.getElementById('avatar').src = canvas.toDataURL('image/png');
  // }

  function startDownload() {
    if (selectedNft.image && !String(selectedNft.image).includes('mp4')) {
      let imageURL = selectedNft.image;
      downloadedImg = new Image();
      downloadedImg.crossOrigin = 'Anonymous';
      // downloadedImg.addEventListener('load', imageReceived, false);
      downloadedImg.src = imageURL;
    }
  }

  useEffect(() => {
    const runId = ++effectRunRef.current;

    const init = async () => {
      const tokenMatch = selectedNft && String(selectedNft.tokenID) === String(tokenId);
      const contractMatch = !resolvedContract || !selectedNft?.contractAddress ||
        String(selectedNft.contractAddress).toLowerCase() === String(resolvedContract).toLowerCase();
      const hasDisplayData = selectedNft?.name && selectedNft?.image;
      const skipFetch = tokenMatch && contractMatch && hasDisplayData;
      if (Number(tokenId) >= 0 && !skipFetch) {
        dispatch(fetchNftInfoAsync(tokenId, resolvedContract));
      }

      try {
        const priceData = await axios.get(
          `https://api.binance.com/api/v3/ticker/price?symbol=BNBUSDT`,
        );
        if (runId !== effectRunRef.current) return;
        const bnbVal = priceData?.data?.price != null ? Number(priceData.data.price) : null;
        if (bnbVal != null) setBNBPrice(bnbVal);
      } catch (e) {
        if (runId !== effectRunRef.current) return;
      }

      if (resolvedContract && String(resolvedContract).startsWith('0x')) {
        try {
          const url = `${process.env.REACT_APP_MARKETPLACE_BACK_URL}/auction?contractAddress=${resolvedContract}&tokenID=${tokenId}`;
          const response = await axios.get(url);
          if (runId !== effectRunRef.current) return;
          const rawItem = response?.data?.items?.[0] || (Array.isArray(response?.data) && response.data[0]);
          const item = rawItem ? { ...rawItem, tokenID: rawItem.tokenID ?? rawItem.tokenId } : null;
          const hasPrice = item && (Number(item.startingPrice) > 0 || Number(item.endingPrice) > 0);
          if (item && hasPrice) {
            const isMCRTFromApi = Boolean(item.isMCRT);
            setNFTData((prev) => {
              const keepPrev = prev && String(prev.tokenID ?? prev.tokenId) === String(tokenId) && (Number(prev.startingPrice) > 0 || Number(prev.endingPrice) > 0);
              return keepPrev ? prev : item;
            });
            setIsMCRT(isMCRTFromApi);
          }
        } catch (err) {
          if (runId !== effectRunRef.current) return;
        }
      }

      try {
        const mcrtPriceData = await axios.get(
          'https://api.coingecko.com/api/v3/simple/price?ids=magiccraft&vs_currencies=usd',
        );
        if (runId !== effectRunRef.current) return;
        const mcrtUsd = mcrtPriceData?.data?.magiccraft?.usd != null ? Number(mcrtPriceData.data.magiccraft.usd) : null;
        if (mcrtUsd != null) setMCRTPrice(mcrtUsd);
      } catch (e) {
        if (runId !== effectRunRef.current) return;
      }

      if (resolvedContract && String(resolvedContract).startsWith('0x')) {
        try {
          const mcrtAuction = await ctMarketplace.methods.isMCRT(resolvedContract, tokenId).call();
          if (runId !== effectRunRef.current) return;
          setIsMCRT((prev) => (prev !== null ? prev : mcrtAuction));
        } catch (e) {
          if (runId !== effectRunRef.current) return;
          setIsMCRT((prev) => prev);
        }
      }

      if (9999 < Number(tokenId)) navigate('/marketplace/profile');
    };

    init();
    // Only run when URL (tokenId, resolvedContract) changes; avoid re-running on selectedNft updates so auction price is not overwritten
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tokenId, resolvedContract]);

  if (!selectedNft || !selectedNft.tokenID) {
    return (
      <div className="nft-detail">
        <div className="nft-detail__spinner" />
      </div>
    );
  }

  const rarity = (selectedNft.attributes || []).find(
    (it) => it.trait_type === 'rarity',
  )?.value;

  // console.log(selectedNft.image, getRarityFrame(rarity))

  // mergeImages([selectedNft.image, getRarityFrame(rarity)])
  // .then((b64) => {
  //   document.getElementsByClassName('abc')[0].src = b64;
  // })
  // .catch(error => console.log(error));

  let attributes = [];

  const nftName = selectedNft.name ?? `NFT #${selectedNft.tokenID ?? ''}`;
  const nftImage = selectedNft.image ?? '';
  const nftDescription = selectedNft.description ?? '';

  const hasAuctionFields = (o) =>
    o && (Number(o.startingPrice) > 0 || Number(o.endingPrice) > 0) && o.duration != null && o.startAt != null;
  const effectiveAuctionData = nftData || (hasAuctionFields(selectedNft) ? {
    startingPrice: selectedNft.startingPrice,
    endingPrice: selectedNft.endingPrice,
    duration: selectedNft.duration,
    startAt: selectedNft.startAt,
    isMCRT: selectedNft.isMCRT,
    seller: selectedNft.seller,
  } : null);


  const attrs = selectedNft.attributes || [];
  for (let i = 1; i <= 3; i++) {
    const defName = attrs.find(
      (it) => it.trait_type === `default_ability_name_${i}`,
    );
    const defValue = attrs.find(
      (it) => it.trait_type === `default_description_${i}`,
    );

    const specName = attrs.find(
      (it) => it.trait_type === `special_name_${i}`,
    );
    const specValue = attrs.find(
      (it) => it.trait_type === `special_description_${i}`,
    );

    if (defName && defValue) {
      attributes.push({ name: defName.value, value: defValue.value });
    }

    if (specName && specValue) {
      attributes.push({ name: specName.value, value: specValue.value });
    }
  }

  const showBNBPrice = (data) => {
    if (!data || data.startingPrice == null || data.endingPrice == null) return null;
    const today = new Date();
    const duration = Number(data.duration) || 1;
    const startAt = Number(data.startAt) || 0;
    const elapsed = Math.max(0, Math.min(duration, Math.ceil(today.getTime() / 1000.0 - startAt)));
    const useMCRT = isMCRT !== null ? isMCRT : Boolean(data.isMCRT);

    let startingPrice = Number(data.startingPrice).toLocaleString('fullwide', {
      useGrouping: false,
    });

    let endingPrice = Number(data.endingPrice).toLocaleString('fullwide', {
      useGrouping: false,
    });

    let amt;

    if (!useMCRT)
      amt = ethers.utils.formatEther(
        ethers.BigNumber.from(`${startingPrice}`).add(
          ethers.BigNumber.from(elapsed)
            .mul(
              ethers.BigNumber.from(`${endingPrice}`).sub(
                ethers.BigNumber.from(`${startingPrice}`),
              ),
            )
            .div(ethers.BigNumber.from(duration)),
        ),
      );
    else
      amt = ethers.utils.formatUnits(
        ethers.BigNumber.from(`${data.startingPrice}`).add(
          ethers.BigNumber.from(elapsed)
            .mul(
              ethers.BigNumber.from(`${data.endingPrice}`).sub(
                ethers.BigNumber.from(`${data.startingPrice}`),
              ),
            )
            .div(ethers.BigNumber.from(duration)),
        ),
        9,
      );

    return (
      <div className="nft-detail__metadata-price">
        <div className="d-flex align-items-center">
          <img loading="lazy" src={useMCRT ? mcrtLogo : bnbLogo} alt="bnb" />
          <span>
            {useMCRT ? Number(amt).toLocaleString() : Number(amt).toFixed(2)}
          </span>
        </div>

        <p className="price-dollar">
          {(() => {
            const price = useMCRT ? mcrtPrice : bnbPrice;
            if (price == null || Number(price) <= 0) return '—';
            return `$${(Number(amt) * Number(price)).toFixed(2)}`;
          })()}
        </p>
      </div>
    );
  };

  const openHelpModal = () => {
    setIsHelpModalOpen(true);
    if (typeof window != 'undefined' && window.document) {
      document.body.style.overflow = 'hidden';
    }
  };

  const closeHelpModal = () => {
    setIsHelpModalOpen(false);
    document.body.style.overflowY = 'unset';
    document.body.style.overflowX = 'hidden';
  };

  return (
    <>
      {isHelpModalOpen && (
        <HelpModal isModalOpen={isHelpModalOpen} closeModal={closeHelpModal} />
      )}
      <div className="container nft-detail">
        <SellModal
          onClose={closeSellModal}
          isSellModal={isSellModal}
          nftItem={selectedNft}
        />

        <div className="nft-detail__back" onClick={goBack}>
          <div className="nft-detail__back-wrapper">
            <img loading="lazy" src="/assets/nft/back.svg" alt="back" />
            <span>Back</span>
          </div>

          {breakpoint.xs && effectiveAuctionData && (
            <div className="nft-detail__back-list">
              <Badge />
              <p>Item listed:</p>
              <strong>
                {effectiveAuctionData?.startAt &&
                  new Date(Number(effectiveAuctionData.startAt) * 1000)
                    .toString()
                    .split(' ')
                    .slice(1, 4)
                    .join(' ')}
              </strong>
            </div>
          )}
        </div>

        <div className="nft-detail__content mobile-top">
          <div className="d-flex align-items-center justify-content-between">
            <h1 className="item-title">{nftName}</h1>
          </div>

          <div className="d-flex">
            <span
              className="hero__knight"
              style={{ backgroundColor: getNameBgColor(nftName) }}
            >
              {(nftName || '').split(',')[1]?.split('The').join('')}
            </span>
            {rarity && (
              <span
                className="rarity__legendary"
                style={{ backgroundColor: getRarityBgColor(rarity) }}
              >
                {rarity}
              </span>
            )}
          </div>

          <p className="item-id">{`#${selectedNft.tokenID}`}</p>
        </div>

        <div className="nft-detail__section">
          <div className="nft-detail__avatar">
            {rarity && nftName !== 'Blazy' && (
              <img
                src={getRarityFrame(rarity)}
                style={{ position: 'absolute', zIndex: '11' }}
                alt="1"
              />
            )}
            <NftMedia src={nftImage || ''} alt="NFT" />
            {/* <img loading="lazy" id="avatar" src="" alt="avatar" /> */}
          </div>

          <div className="nft-detail__metadata">
            <div
              className={`nft-detail__metadata__wrapper container ${
                isOwned && 'align-items-center justify-content-end w-100'
              }`}
            >
              {!isOwned && (
                <div className="nft-detail__metadata-owner">
                  <p className="text-owner">OWNER</p>
                  <p className="text-owner__address">
                    {getShortenAddress(
                      effectiveAuctionData ? effectiveAuctionData.seller : selectedNft.owner,
                    )}
                  </p>
                </div>
              )}

              {isOwned && effectiveAuctionData && (
                <div className="nft-detail__metadata-list">
                  <Badge />
                  <div>
                    <p>Item listed:</p>
                    {/* <strong>May 20 2022</strong> */}
                    <strong>
                      {effectiveAuctionData?.startAt &&
                        new Date(Number(effectiveAuctionData.startAt) * 1000)
                          .toString()
                          .split(' ')
                          .slice(1, 4)
                          .join(' ')}
                    </strong>
                  </div>
                </div>
              )}

              {isOwned ? (
                effectiveAuctionData ? (
                  <div className="nft-detail__metadata-price__wrapper">
                    {showBNBPrice(effectiveAuctionData)}
                    <Button className="danger" onClick={onCancelItem}>
                      Cancel Listing
                    </Button>
                  </div>
                ) : (
                  <Button variant="primary" onClick={onSellItem}>
                    Sell Item
                  </Button>
                )
              ) : (
                effectiveAuctionData && (
                  <div className="nft-detail__metadata-price__wrapper">
                    {showBNBPrice(effectiveAuctionData)}
                    <Button variant="primary" onClick={onBuyItem}>
                      Buy Item
                    </Button>
                  </div>
                )
              )}
            </div>

            <div className="nft-detail__content">
              <div className="container nft-detail__content__wrapper">
                <div className="d-flex align-items-center justify-content-between">
                  <h1 className="item-title">{nftName}</h1>
                  <span className="item-id">{`#${selectedNft.tokenID}`}</span>
                </div>

                {rarity && (
                  <div className="d-flex">
                    {/* <span
                      className="hero__knight"
                      style={{
                        backgroundColor: nftName.includes('Knight')
                          ? '#C41E3A'
                          : nftName.includes('Hunter')
                          ? '#AAD372'
                          : '#3FC7EB',
                      }}
                    ></span> */}
                    <span
                      className="rarity__legendary"
                      style={{ backgroundColor: getRarityBgColor(rarity) }}
                    >
                      {rarity}
                    </span>
                  </div>
                )}
              </div>

              <div>
                <h3 className="item-description-text">DESCRIPTION</h3>
                <p className="item-description">{nftDescription}</p>
              </div>

              {attributes.length ? (
                <div className="nft-detail__ability">
                  <h3 className="item-ability">Abilities</h3>
                  <div className="nft-detail__ability-content">
                    {/* Ability Item */}
                    {attributes.map((it) => {
                      return (
                        <div className="nft-detail__ability-item" key={it.name}>
                          <div className="nft-detail__ability-item__image">
                            <img
                              src={`/assets/nft/defensive_stance.png`}
                              alt="attribute-img"
                            />
                          </div>

                          <div className="nft-detail__ability-item__text">
                            <p className="item-ability-type">{it.name}</p>
                            <p className="item-ability-value">{it.value}</p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ) : null}
            </div>

            <GasFeeHelpContainer
              content="Experiencing high gas fees?"
              openStakingHelpModal={openHelpModal}
            />
          </div>
        </div>
      </div>

      {!isOwned ? (
        <div className="container">
          <div className="nft-detail__metadata__wrapper tablet">
            <div className="nft-detail__metadata-owner">
              <p className="text-owner">OWNER</p>
              <p className="text-owner__address">
                {`(${getShortenAddress(
                  effectiveAuctionData ? effectiveAuctionData.seller : selectedNft.owner,
                )})`}
              </p>
            </div>
            {effectiveAuctionData && (
              <div className="nft-detail__metadata-price__wrapper">
                {showBNBPrice(effectiveAuctionData)}
                <Button variant="primary" onClick={onBuyItem}>
                  Buy Item
                </Button>
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="container">
          <div className="nft-detail__metadata__wrapper tablet">
            <div
              className={`d-flex align-items-center justify-content-${
                effectiveAuctionData ? 'between' : 'center'
              } w-100`}
            >
              {effectiveAuctionData && (
                <div className="nft-detail__metadata-list">
                  <Badge />
                  <div>
                    <p>Item listed:</p>
                    <strong>
                      {effectiveAuctionData.startAt &&
                        new Date(Number(effectiveAuctionData.startAt) * 1000)
                          .toString()
                          .split(' ')
                          .slice(1, 4)
                          .join(' ')}
                    </strong>
                  </div>
                </div>
              )}

              {effectiveAuctionData ? (
                <div className="nft-detail__metadata-price__wrapper tablet">
                  {showBNBPrice(effectiveAuctionData)}
                  <Button className="danger" onClick={onCancelItem}>
                    Cancel Listing
                  </Button>
                </div>
              ) : (
                <Button variant="primary" onClick={onSellItem}>
                  Sell Item
                </Button>
              )}
            </div>
          </div>
        </div>
      )}

      {!isOwned ? (
        <div className="container">
          <div className="nft-detail__metadata-owner mobile">
            <p className="text-owner">OWNER</p>
            <p className="text-owner__address">
              {effectiveAuctionData ? effectiveAuctionData.seller : selectedNft.owner}
            </p>
          </div>
          <div className="nft-detail__metadata__wrapper mobile">
            {effectiveAuctionData && (
              <div className="nft-detail__metadata-price__wrapper mobile">
                {showBNBPrice(effectiveAuctionData)}
                <Button variant="primary" onClick={onBuyItem}>
                  Buy Item
                </Button>
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="">
          <div className="nft-detail__metadata__wrapper mobile">
            <div
              className={`d-flex align-items-center justify-content-${
                effectiveAuctionData ? 'between' : 'center'
              } w-100`}
            >
              {effectiveAuctionData ? (
                <div className="nft-detail__metadata-price__wrapper mobile">
                  {showBNBPrice(effectiveAuctionData)}
                  <Button className="danger" onClick={onCancelItem}>
                    Cancel Listing
                  </Button>
                </div>
              ) : (
                <Button variant="primary" onClick={onSellItem}>
                  Sell Item
                </Button>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default NFTDetail;
